import { Button } from '@recipes/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { Workbox } from 'workbox-window';

/** An installed app stays open for days: look for a new version without a page load. */
const UPDATE_CHECK_MS = 60 * 60 * 1000;

/**
 * Controlled "New version available" prompt. The service worker never takes over an
 * active cooking session: while the user is on /cook/* the prompt is deferred.
 *
 * The browser looks for a new service worker when the app is opened. An installed app is
 * rarely "opened" – it is brought back to the front – so this also asks every hour and whenever
 * the app becomes visible again. A new version is downloaded in the background and offered
 * here; nothing is replaced under the user's hands.
 */
export function ServiceWorkerPrompt() {
  const { t } = useTranslation();
  const location = useLocation();
  const [wb, setWb] = useState<Workbox | null>(null);
  const [waiting, setWaiting] = useState(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator) || import.meta.env.DEV) return;
    const workbox = new Workbox('/sw.js');
    workbox.addEventListener('waiting', () => setWaiting(true));
    workbox.addEventListener('controlling', () => window.location.reload());
    void workbox.register();
    setWb(workbox);

    const check = () => {
      if (navigator.onLine) void workbox.update().catch(() => undefined);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    const timer = window.setInterval(check, UPDATE_CHECK_MS);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const inCookingMode = location.pathname.startsWith('/cook');
  if (!waiting || inCookingMode) return null;

  return (
    <div
      role="status"
      className="fixed inset-x-0 bottom-16 z-40 mx-auto flex max-w-md items-center justify-between gap-3 rounded-lg border bg-card p-3 shadow-lg md:bottom-4"
    >
      <span className="text-sm">{t('app.updateAvailable')}</span>
      <div className="flex gap-2">
        <Button size="sm" variant="ghost" onClick={() => setWaiting(false)}>
          {t('app.updateLater')}
        </Button>
        <Button size="sm" onClick={() => wb?.messageSkipWaiting()}>
          {t('app.updateNow')}
        </Button>
      </div>
    </div>
  );
}
